// Line icons in the SF Symbols spirit: a 24 box, 1.9 stroke, round caps.
// Each icon is a list of shapes: a path's d, a circle ['c', cx, cy, r] or a
// rect ['r', x, y, w, h, rx]. Decorative unless given a label.
import type { SVGProps } from 'react';
import { cx } from './cx';

type Shape = string | readonly ['c', number, number, number] | readonly ['r', number, number, number, number, number];

const ICONS = {
  gear: ['M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z', 'M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z'],
  plus: ['M12 5v14M5 12h14'],
  back: ['M15 5l-7 7 7 7'],
  chev: ['M9 5l7 7-7 7'],
  up: ['M5 15l7-7 7 7'],
  down: ['M5 9l7 7 7-7'],
  check: ['M4 12.5l5 5L20 7'],
  x: ['M6 6l12 12M18 6L6 18'],
  camera: ['M3 8.5A1.5 1.5 0 0 1 4.5 7H7l1.5-2.5h7L17 7h2.5A1.5 1.5 0 0 1 21 8.5V18a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18Z', ['c', 12, 13, 3.5]],
  share: ['M12 3v12M8 7l4-4 4 4', 'M5 11v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8'],
  wand: ['M4 20L15 9', 'M15 4l1 2 2 1-2 1-1 2-1-2-2-1 2-1Z', 'M19 11l.6 1.2 1.4.6-1.4.6L19 15l-.6-1.2-1.4-.6 1.4-.6Z', 'M8 3l.5 1 1 .5-1 .5L8 6l-.5-1-1-.5 1-.5Z'],
  list: [['c', 5, 7, 2], 'M11 7h9', ['c', 5, 17, 2], 'M11 17h9', 'M3.8 7l1 1 2-2'],
  tools: ['M14.5 6.5a3.5 3.5 0 0 0 4.3 4.3L21 13 13 21l-2.2-2.2 3.5-3.5', 'M3 21l6-6', 'M9.5 9.5 3 3l4 1 2.5 2.5'],
  bulb: ['M9 18h6M10 21h4', 'M12 3a6 6 0 0 0-3.5 10.9c.8.6 1.5 1.5 1.5 2.1h4c0-.6.7-1.5 1.5-2.1A6 6 0 0 0 12 3Z'],
  globe: [['c', 12, 12, 9], 'M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18'],
  play: ['M7 4l12 8-12 8Z'],
  bell: ['M6 16V11a6 6 0 0 1 12 0v5l2 2H4Z', 'M10 21h4'],
  key: [['c', 8, 14, 4], 'M11 11l9-9M16 6l2 2M13 9l2 2'],
  person: [['c', 12, 8, 4], 'M4 21a8 8 0 0 1 16 0'],
  link: ['M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1', 'M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1'],
  copy: [['r', 9, 9, 11, 11, 2], 'M5 15V5a2 2 0 0 1 2-2h10'],
  card: [['r', 3, 6, 18, 13, 2], 'M3 10h18M7 15h4'],
  face: ['M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M4 16v3a1 1 0 0 0 1 1h3M16 20h3a1 1 0 0 0 1-1v-3', 'M9 10v1M15 10v1M9.5 15a3.5 3.5 0 0 0 5 0'],
  phone: [['r', 7, 3, 10, 18, 2], 'M11 18h2'],
  home: ['M4 11l8-7 8 7v9a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1Z'],
  squareUp: ['M12 3v12M8 7l4-4 4 4', ['r', 4, 11, 16, 10, 2]],
  dots: [['c', 6, 12, 1.6], ['c', 12, 12, 1.6], ['c', 18, 12, 1.6]],
  eye: ['M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z', ['c', 12, 12, 3]],
  target: [['c', 12, 12, 9], ['c', 12, 12, 4], 'M12 3v3M12 18v3M3 12h3M18 12h3'],
  trash: ['M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13'],
  pen: ['M4 20l4-1L19 8l-3-3L5 16Z'],
  rotate: ['M20 12a8 8 0 1 1-2.3-5.7', 'M20 4v4h-4'],
  bolt: ['M13 2L4 14h7l-1 8 9-12h-7Z'],
  sun: [['c', 12, 12, 4], 'M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5 19 19M5 19l1.5-1.5M17.5 6.5 19 5'],
  wifiOff: ['M2 9a16 16 0 0 1 20 0M5 12.5a11 11 0 0 1 14 0M8.5 16a6 6 0 0 1 7 0', ['c', 12, 19.5, 1], 'M3 3l18 18'],
  print: ['M7 8V3h10v5M7 17H4a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-3', ['r', 7, 14, 10, 7, 0]],
  mail: [['r', 3, 5, 18, 14, 2], 'M3 7l9 6 9-6'],
  shield: ['M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6Z', 'M9 12l2 2 4-4'],
  chart: ['M4 20V10M10 20V4M16 20v-8M22 20H2'],
  receipt: ['M6 3h12v18l-2-1.5L14 21l-2-1.5L10 21l-2-1.5L6 21Z', 'M9 8h6M9 12h6'],
  undo: ['M9 14 4 9l5-5', 'M4 9h10a6 6 0 0 1 0 12h-3'],
  download: ['M12 3v12M8 11l4 4 4-4', 'M4 19h16'],
  pause: [['r', 6, 4, 4, 16, 1], ['r', 14, 4, 4, 16, 1]],
  sparkle: ['M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2Z'],
  paperclip: ['M21 11.5 12 20.5a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-3-3l8-8'],
  ribbon: [['c', 12, 9, 6], 'M8 14l-2 7 6-3 6 3-2-7'],
} as const satisfies Record<string, readonly Shape[]>;

export type IconName = keyof typeof ICONS;
export const iconNames = Object.keys(ICONS) as IconName[];

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** Only when nothing beside the icon says what it means; otherwise it is decoration. */
  label?: string;
  size?: number;
}

export function Icon({ name, label, size, className, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round"
      width={size} height={size} className={cx('icon', className)}
      role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}
      {...rest}
    >
      {ICONS[name].map((s, i) =>
        typeof s === 'string' ? <path key={i} d={s} />
          : s[0] === 'c' ? <circle key={i} cx={s[1]} cy={s[2]} r={s[3]} />
            : <rect key={i} x={s[1]} y={s[2]} width={s[3]} height={s[4]} rx={s[5]} />)}
    </svg>
  );
}
